import { Location, ItineraryItem } from "@/types/trip";
import { calculateStartTime } from "@/lib/experiences/newExperience";
import useToraStore from "@/stores/tripBuilder/useToraStore";
import { ServerFlightInfo } from "~/types/flights";
import logger from "@/utils/logger";

interface EntityChunk {
  date: string;
  entity: ItineraryItem;
  start: number;
  end: number;
  action: "add" | "del" | "edit" | null;
}

interface TripChangeAction {
  type: "trip_change";
  trip_change: {
    operation: "remove" | "add";
    trip_id: string;
    itinerary_changed: boolean;
    deleted_itinerary_event_ids?: Array<{
      date: string;
      id: string;
    }>;
    added_itinerary_events?: Array<{
      date: string;
      item: ItineraryItem;
    }>;
  };
}

interface FlightResult {
  id: string;
  price: number;
  currency: string;
  departure: string;
  arrival: string;
  from: string;
  to: string;
  cities: { from: string; to: string };
  airlines: string[];
  duration: { departure: number; return: number; total: number };
  route: Array<{
    airline: string;
    flight: number;
    from: string;
    to: string;
    times: { dep: string; arr: string };
  }>;
}

interface StreamChunk {
  type: "actionable_item" | "text" | "action" | "flight_search_results";
  data: {
    action?: "suggest" | "add_to_itinerary" | "remove_from_itinerary";
    description?: string;
    id?: string;
    image?: { url: string; isMain: boolean; source: string };
    name?: string;
    types?: string[];
    date?: string;
    suggestedStartTime?: string;
    duration?: number;
    pricing?: number;
    results?: ServerFlightInfo[];
    latitude?: number;
    longitude?: number;
  };
}

export interface TextHighlight {
  start: number;
  end: number;
  entityName: string;
}

export class StreamHandler {
  private location: Location;
  private updateLocation: (updates: Partial<Location>) => void;
  private onHighlight?: (highlight: TextHighlight) => void;

  constructor(
    location: Location,
    updateLocation: (updates: Partial<Location>) => void,
    onHighlight?: (highlight: TextHighlight) => void
  ) {
    this.location = location;
    this.updateLocation = updateLocation;
    this.onHighlight = onHighlight;
  }

  handleChunk(chunk: StreamChunk) {
    if (
      chunk.type === "flight_search_results" &&
      Array.isArray(chunk.data.results)
    ) {
      this.handleFlightResults(chunk.data.results);
      return;
    }

    if (chunk.data.action === "suggest") {
      this.handleSuggestion(
        chunk.data.id,
        chunk.data.name,
        chunk.data.description,
        chunk.data.types,
        chunk.data.image
      );
      return;
    }

    if (chunk.data.action === "add_to_itinerary" && chunk.data.date) {
      this.handleAddToItinerary(
        chunk.data.date,
        chunk.data.id,
        chunk.data.name,
        chunk.data.description,
        chunk.data.suggestedStartTime,
        chunk.data.duration,
        chunk.data.types,
        chunk.data.image,
        chunk.data.latitude,
        chunk.data.longitude
      );
      return;
    }

    if (
      chunk.data.action === "remove_from_itinerary" &&
      chunk.data.date &&
      chunk.data.id
    ) {
      this.removeExperienceFromItinerary(chunk.data.date, chunk.data.id);
      return;
    }

    // Handle any other types of chunks here
  }

  private handleFlightResults(results: ServerFlightInfo[]) {
    const { addFlightRecommendations, setShowRecommendations } =
      useToraStore.getState();

    logger.log("FLIGHT RESULTS", results);

    if (results.length > 0) {
      addFlightRecommendations(results);
      setShowRecommendations(true);
    }
  }

  private handleSuggestion(
    id?: string,
    name?: string,
    description?: string,
    types?: string[],
    image?: { url: string; isMain: boolean; source: string }
  ) {
    const { addSuggestion, showRecommendations, setShowRecommendations } =
      useToraStore.getState();

    if (!showRecommendations) {
      setShowRecommendations(true);
    }

    // Only add if we have the required fields
    if (id && name && description && image) {
      // Add to both legacy suggestions and typed experiences
      const suggestion = {
        id,
        name,
        description,
        types: types || [],
        image,
      };

      addSuggestion(suggestion);
    }
  }

  private handleAddToItinerary(
    date: string,
    id?: string,
    name?: string,
    description?: string,
    suggestedStartTime?: string,
    duration?: number,
    types?: string[],
    image?: { url: string; isMain: boolean; source: string },
    latitude?: number,
    longitude?: number
  ) {
    // Only proceed if we have the minimum required fields
    if (!id || !name) return;

    const newItem: ItineraryItem = {
      id,
      name,
      start_time: suggestedStartTime || null,
      duration: duration || null,
      images: image ? [image.url] : [],
      coordinates: { latitude: latitude || 0, longitude: longitude || 0 },
      metadata: {
        description: description || "",
      },
      pricing: 0,
      type: "waytora",
      sourceId: {
        waytoraPlaceId: id,
      },
      bookingStatus: "not_booked",
      categories: types || [],
      participants: null,
    };

    this.addExperienceToItinerary(date, newItem);
  }

  private async addExperienceToItinerary(date: string, item: ItineraryItem) {
    logger.log("Adding experience to itinerary", item);

    // Find the day's itinerary
    let dayItinerary = this.location.itinerary_items.find(
      (day) => day.date.split("T")[0] === date.split("T")[0]
    );

    if (!dayItinerary) {
      // Create new day if it doesn't exist
      dayItinerary = {
        date: date,
        items: [],
      };
      this.location.itinerary_items.push(dayItinerary);
    }

    // Check if item already exists
    const existingItemIndex = dayItinerary.items.findIndex(
      (existingItem) => existingItem.id === item.id
    );

    if (existingItemIndex === -1) {
      // Add new item
      dayItinerary.items.push(item);
    } else {
      // Update existing item
      dayItinerary.items[existingItemIndex] = item;
    }

    // Update location with modified itinerary items
    this.updateLocation({
      itinerary_items: [...this.location.itinerary_items],
    });
  }

  private removeExperienceFromItinerary(date: string, id: string) {
    logger.log("Removing experience from itinerary", id);

    // Find the day's itinerary
    const dayItinerary = this.location.itinerary_items.find(
      (day) => day.date.split("T")[0] === date.split("T")[0]
    );

    if (!dayItinerary) {
      logger.log("Day itinerary not found");
      return;
    }

    // Find the index of the item to remove
    const itemIndex = dayItinerary.items.findIndex((item) => item.id === id);

    if (itemIndex === -1) {
      logger.log("Item not found in itinerary");
      return;
    }

    // Remove the item from the itinerary
    dayItinerary.items.splice(itemIndex, 1);

    // Update location with modified itinerary items
    this.updateLocation({
      itinerary_items: [...this.location.itinerary_items],
    });
  }

  private handleTripChange(action: TripChangeAction) {
    if (
      action.trip_change.deleted_itinerary_event_ids &&
      action.trip_change.operation === "remove"
    ) {
      this.removeDeletedItems(action.trip_change.deleted_itinerary_event_ids);
    } else if (
      action.trip_change.added_itinerary_events &&
      action.trip_change.operation === "add"
    ) {
      this.addNewItems(action.trip_change.added_itinerary_events);
    }
  }

  private removeDeletedItems(
    deletedItems: Array<{ date: string; id: string | number }>
  ) {
    logger.log("deletedItems", deletedItems);
    // Create a new array to hold the updated itinerary items
    const updatedItineraryItems = this.location.itinerary_items.map(
      (dayItinerary) => {
        // For each day, filter out the deleted items
        const deletedIdsForDay = deletedItems
          .filter((item) => {
            // Convert both dates to YYYY-MM-DD format for comparison
            const itemDate = item.date.split("T")[0];
            const dayDate = dayItinerary.date.split("T")[0];
            return itemDate === dayDate;
          })
          .map((item) => item.id.toString()); // Convert all IDs to strings

        logger.log("deletedIdsForDay", deletedIdsForDay);
        logger.log("dayItinerary before", dayItinerary);

        const updatedDay = {
          ...dayItinerary,
          items: dayItinerary.items.filter(
            (item) => !deletedIdsForDay.includes(item.id.toString()) // Convert item.id to string for comparison
          ),
        };

        logger.log("dayItinerary after", updatedDay);
        return updatedDay;
      }
    );

    // Update the location with the filtered itinerary items
    this.updateLocation({
      itinerary_items: updatedItineraryItems,
    });
  }

  private async addNewItems(
    newItems: Array<{ date: string; item: ItineraryItem }>
  ) {
    logger.log("newItems", newItems);
    // Create a new array to hold the updated itinerary items
    const updatedItineraryItems = [...this.location.itinerary_items];

    // Process each new item
    for (const { date, item } of newItems) {
      // Normalize the date to YYYY-MM-DD format
      const normalizedDate = date.split("T")[0];

      // Find the day's itinerary
      let dayItinerary = updatedItineraryItems.find((day) => {
        const dayDate = day.date.split("T")[0];
        return dayDate === normalizedDate;
      });

      if (!dayItinerary) {
        // Create new day if it doesn't exist
        dayItinerary = {
          date: date, // Keep original date format
          items: [],
        };
        updatedItineraryItems.push(dayItinerary);
      }

      logger.log("dayItinerary", dayItinerary);

      // Calculate start time if it's not set
      if (!item.start_time) {
        try {
          logger.log("calculating start time for item", item);
          item.start_time = await calculateStartTime(
            dayItinerary,
            this.location.date.time_zone
          );
        } catch (error) {
          console.error("Failed to calculate start time:", error);
        }
      }

      // Check if item already exists
      const existingItemIndex = dayItinerary.items.findIndex(
        (existingItem) => existingItem.id === item.id
      );

      logger.log("existingItemIndex", existingItemIndex);

      if (existingItemIndex === -1) {
        // Add new item
        dayItinerary.items.push(item);
      } else {
        // Update existing item
        dayItinerary.items[existingItemIndex] = item;
      }
    }

    // Update location with modified itinerary items
    this.updateLocation({
      itinerary_items: updatedItineraryItems,
    });
  }
}
